import { useAui } from "@assistant-ui/react";
import { useMutation } from "@tanstack/react-query";
import {
    useCallback,
    useEffect,
    useId,
    useState,
    type FC,
    type KeyboardEvent,
} from "react";
import { toast } from "sonner";

import { dismissEnrichment } from "@/api/chat/chat";
import type { IContextEnrichmentDataPart } from "@/api/chat/chat.types";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { useEnrichmentState } from "../contexts/EnrichmentStateContext";
import { useRefineContext } from "../contexts/RefineContext";
import { synthesizeRefinementPrompt } from "../utils/synthesize-refinement-prompt";

export interface ContextEnrichmentCardProps {
    data: IContextEnrichmentDataPart;
    conversationId: number;
    /** True while the assistant message is still streaming; disables Refine. */
    isStreaming?: boolean;
}

const ONBOARDING_COPY =
    "Help Polly tailor this answer. Tap a quick context or add your own.";
const FREE_TEXT_MAX = 500;
const COUNTER_VISIBILITY_THRESHOLD = Math.floor(FREE_TEXT_MAX * 0.7);
const COUNTER_APPROACHING_THRESHOLD = Math.floor(FREE_TEXT_MAX * 0.9);

// Compute once: navigator is stable for the session and the check is a
// cheap UA sniff. Previously inlined in render, moved out to avoid
// re-running the regex on every keystroke.
const IS_MAC =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad/i.test(navigator.userAgent);
const KBD_HINT = IS_MAC ? "⌘↵" : "Ctrl+↵";
const ARIA_KEY_SHORTCUT = IS_MAC ? "Meta+Enter" : "Control+Enter";

const CHIP_BASE_CLASSES =
    "rounded-full px-3 py-1.5 text-sm font-medium transition-colors duration-150 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1 focus-visible:ring-offset-background";
const CHIP_SELECTED_CLASSES =
    "bg-primary/10 text-primary ring-1 ring-primary/25 hover:bg-primary/15";
const CHIP_UNSELECTED_CLASSES =
    "bg-muted text-muted-foreground hover:bg-muted/70 hover:text-foreground";

/**
 * Refine-your-answer card rendered for `context-enrichment` data parts.
 *
 * Renders the chip + free-text UI when the card is the latest actionable
 * in the conversation, collapses to a "Refined above" footer when
 * `refined=true`, and renders nothing when dismissed or superseded.
 *
 * Clicking Refine synthesizes a first-person user prompt and submits it
 * as a normal composer-sent turn; `RefineContext` carries the source
 * message id one-shot to `chat-model-adapter` so the backend can
 * atomically flip `refined=true` on the source before streaming.
 */
export const ContextEnrichmentCard: FC<ContextEnrichmentCardProps> = ({
    data,
    conversationId,
    isStreaming = false,
}) => {
    const fieldId = useId();
    const aui = useAui();
    const { setRefinedFromMessageId, subscribeRefineFailed } =
        useRefineContext();
    const {
        isDismissed,
        isRefined,
        markDismissed,
        unmarkDismissed,
        markRefined,
        unmarkRefined,
    } = useEnrichmentState();
    const [selectedValues, setSelectedValues] = useState(
        () => new Set<string>()
    );
    const [freeText, setFreeText] = useState("");

    // Session-scoped optimistic state for this card's messageId. Lives in
    // `EnrichmentStateContext` (above `RuntimeProvider` in the tree) so it
    // survives the card's unmount/remount when the user navigates to
    // another thread and back. Assistant-ui reuses the cached thread
    // runtime on return and does not re-run `history.load()`, so the
    // server-loaded `data.dismissed` / `data.refined` is stale until a
    // full page refresh. Either source marks the state terminal.
    const persistedDismissed =
        data.messageId !== undefined && isDismissed(data.messageId);
    const persistedRefined =
        data.messageId !== undefined && isRefined(data.messageId);

    const dismissMutation = useMutation({
        mutationFn: async () => {
            if (data.messageId === undefined) {
                throw new Error("Missing messageId on enrichment data part");
            }
            return dismissEnrichment(conversationId, data.messageId);
        },
        onMutate: () => {
            if (data.messageId !== undefined) markDismissed(data.messageId);
        },
        onError: () => {
            if (data.messageId !== undefined) unmarkDismissed(data.messageId);
            toast.error("Couldn’t dismiss the card. Please try again.");
        },
    });

    // Roll back the optimistic collapse if the adapter reports a
    // pre-stream failure for our message id. Without this the card
    // would stay stuck on "Refined above" for the rest of the session
    // even though the backend atomic bundle never committed.
    useEffect(() => {
        if (!persistedRefined || data.messageId === undefined) return;
        const messageId = data.messageId;
        return subscribeRefineFailed((failedMessageId) => {
            if (failedMessageId !== messageId) return;
            unmarkRefined(messageId);
            toast.error("Couldn’t refine the answer. Please try again.");
        });
    }, [
        persistedRefined,
        data.messageId,
        subscribeRefineFailed,
        unmarkRefined,
    ]);

    const hasSelection = selectedValues.size > 0 || freeText.trim() !== "";
    // `persistedRefined` is part of the disabled set so a fast second click
    // (or Cmd+Enter fired while the button is still mounted pre-render)
    // can't fire a second `aui.thread().append(...)`. The server would 409
    // on the piggyback, but the thread would already have two user turns.
    const refineDisabled =
        !hasSelection ||
        isStreaming ||
        dismissMutation.isPending ||
        persistedRefined;

    const submitRefine = useCallback(() => {
        if (refineDisabled) return;
        if (data.messageId === undefined) {
            toast.error("Couldn’t refine the answer. Please try again.");
            return;
        }
        const messageId = data.messageId;
        const selectedChips = data.chips.filter((c) =>
            selectedValues.has(c.value)
        );
        const synthesized = synthesizeRefinementPrompt({
            chips: selectedChips,
            freeText,
        });
        if (!synthesized) return;

        markRefined(messageId);
        try {
            // Write the ref BEFORE calling append so chat-model-adapter
            // reads-and-clears the id on the very next run() tick.
            setRefinedFromMessageId(messageId);
            aui.thread().append({
                role: "user",
                content: [{ type: "text", text: synthesized }],
            });
        } catch {
            // Roll back the ref so a later composer-sent user turn
            // doesn't inherit this refine marker; otherwise the
            // backend would flip `refined=true` on an unrelated message.
            setRefinedFromMessageId(undefined);
            unmarkRefined(messageId);
            toast.error("Couldn’t refine the answer. Please try again.");
        }
    }, [
        refineDisabled,
        data.messageId,
        data.chips,
        selectedValues,
        freeText,
        setRefinedFromMessageId,
        markRefined,
        unmarkRefined,
        aui,
    ]);

    const onTextareaKeyDown = useCallback(
        (event: KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key !== "Enter") return;
            if (!(event.metaKey || event.ctrlKey)) return;
            event.preventDefault();
            submitRefine();
        },
        [submitRefine]
    );

    const toggleChip = useCallback((value: string) => {
        setSelectedValues((prev) => {
            const next = new Set(prev);
            if (next.has(value)) next.delete(value);
            else next.add(value);
            return next;
        });
    }, []);

    // refined wins over dismissed: both are terminal, but the positive
    // state should remain visible (the footer acknowledges the refinement).
    // Server truth (`data.*`) OR session-scoped optimistic state from the
    // store; either marks the card terminal. See EnrichmentStateContext.
    const refined = data.refined || persistedRefined;
    const dismissed = data.dismissed || persistedDismissed;

    if (refined) {
        // `role="status"` + `aria-live="polite"` announces the transition
        // to screen readers without moving focus. Keyboard focus falls to
        // its next natural target (composer, next message), which is less
        // disruptive than forcing focus onto a non-interactive div.
        return (
            <div
                role="status"
                aria-live="polite"
                className="text-muted-foreground mt-2 text-xs"
            >
                <span aria-hidden="true" className="text-sidebar-primary">
                    ✓
                </span>{" "}
                Refined above
            </div>
        );
    }

    if (dismissed || !data.isLatestActionable) {
        return null;
    }

    const freeTextCounterVisible =
        freeText.length >= COUNTER_VISIBILITY_THRESHOLD;
    const freeTextApproachingMax =
        freeText.length >= COUNTER_APPROACHING_THRESHOLD;
    const refineTooltip = isStreaming
        ? "Wait for the answer to finish"
        : hasSelection
          ? `Refine (${KBD_HINT})`
          : "Tap a chip or add context to refine";

    return (
        <Card
            role="group"
            aria-label="Refine Polly's answer"
            className="bg-primary/[0.035] border-primary/15 dark:bg-primary/[0.08] dark:border-primary/25 mt-3 gap-3 p-4"
        >
            <p className="text-muted-foreground border-sidebar-primary/30 border-b pb-3 text-xs">
                {ONBOARDING_COPY}
            </p>

            <div
                role="group"
                aria-label="Quick context for Polly"
                className="flex flex-wrap gap-2"
            >
                {data.chips.map((chip) => {
                    const selected = selectedValues.has(chip.value);
                    return (
                        <button
                            key={chip.value}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => toggleChip(chip.value)}
                            className={`${CHIP_BASE_CLASSES} ${
                                selected
                                    ? CHIP_SELECTED_CLASSES
                                    : CHIP_UNSELECTED_CLASSES
                            }`}
                        >
                            {chip.label}
                        </button>
                    );
                })}
            </div>

            <div className="flex flex-col gap-1.5">
                <Label
                    htmlFor={fieldId}
                    className="text-muted-foreground text-xs font-medium"
                >
                    {data.freeTextPrompt || "Add more context (optional)"}
                </Label>
                <Textarea
                    id={fieldId}
                    value={freeText}
                    onChange={(e) => setFreeText(e.target.value)}
                    onKeyDown={onTextareaKeyDown}
                    maxLength={FREE_TEXT_MAX}
                    rows={2}
                    placeholder="Anything else?"
                />
                <span
                    aria-hidden={!freeTextCounterVisible}
                    className={`self-end text-xs tabular-nums transition duration-150 ${
                        freeTextCounterVisible
                            ? `opacity-100 ${freeTextApproachingMax ? "text-foreground" : "text-muted-foreground"}`
                            : "opacity-0"
                    }`}
                >
                    {freeText.length}/{FREE_TEXT_MAX}
                </span>
            </div>

            <div className="flex items-center justify-end gap-2">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => dismissMutation.mutate()}
                    disabled={dismissMutation.isPending}
                    aria-busy={dismissMutation.isPending}
                >
                    Dismiss
                </Button>
                <Button
                    type="button"
                    size="sm"
                    aria-keyshortcuts={ARIA_KEY_SHORTCUT}
                    title={refineTooltip}
                    onClick={submitRefine}
                    disabled={refineDisabled}
                    className="hover:bg-primary/80"
                >
                    Refine
                    <span
                        aria-hidden="true"
                        className="ml-2 font-mono text-[11px] opacity-75"
                    >
                        {KBD_HINT}
                    </span>
                </Button>
            </div>
        </Card>
    );
};
